// ABOUTME: Hook to fetch model selection data via POST. Products use /api/model-selection/, services use /api/model-selection/service-contract.
// ABOUTME: Returns data, loading, error states, and lazy-loading callbacks for subranges, search, and filters.

import type { ICategory, IFilter, IModelSelectionData } from '@coolshop/cool-model-selection';
import { useCallback, useMemo, useState } from 'react';

import { ITEM_TYPE, ItemType } from '../types';

async function fetchModelSelection(
    endpoint: string,
    body: Record<string, unknown> = {},
): Promise<Response> {
    return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
}

export function useModelSelectionData(itemType?: ItemType) {
    const [data, setData] = useState<IModelSelectionData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Services are served by a separate endpoint (its non-CGT branch builds the item_type='service'
    // catalog via createModelSelectionDocumentService); products use the standard catalog endpoint.
    const endpoint = useMemo(
        () =>
            itemType === ITEM_TYPE.service
                ? '/api/model-selection/service-contract'
                : '/api/model-selection/',
        [itemType],
    );

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchModelSelection(endpoint);
            if (!response.ok) {
                throw new Error(`Failed to fetch model selection data: ${response.statusText}`);
            }
            const json = await response.json();
            const categories: ICategory[] = json.categories ?? json ?? [];

            const modelSelectionData: IModelSelectionData = {
                id: 'cpq-prototype-selection',
                categories,
                fdps: [],
                mainFdps: [],
                techTypeToModelId: {},
                description: null,
            };

            setData(modelSelectionData);
        } catch (err) {
            const message =
                err instanceof Error ? err.message : 'Failed to load model selection data';
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [endpoint]);

    const loadSubranges = useCallback(
        async ({ range }: { range: ICategory }): Promise<ICategory[] | undefined> => {
            try {
                const response = await fetchModelSelection(endpoint, { range: range.id });
                if (!response.ok) return undefined;
                const json = await response.json();
                return (json as ICategory[]) ?? undefined;
            } catch {
                return undefined;
            }
        },
        [endpoint],
    );

    const loadSearchResults = useCallback(
        async ({ searchText }: { searchText: string }): Promise<ICategory[] | undefined> => {
            try {
                const response = await fetchModelSelection(endpoint, { searchText });
                if (!response.ok) return undefined;
                const json = await response.json();
                return (json as ICategory[]) ?? undefined;
            } catch {
                return undefined;
            }
        },
        [endpoint],
    );

    // Filters use the shared product-catalog endpoint regardless of itemType: the service-contract
    // router does not expose a separate /filters path, and the filter taxonomy is common to both
    // product and service catalogs.
    const loadFilters = useCallback(
        async ({ categoryId }: { categoryId?: string }): Promise<IFilter[] | undefined> => {
            try {
                const response = await fetch('/api/model-selection/filters', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(categoryId ? { categoryId } : {}),
                });
                if (!response.ok) return undefined;
                const json = await response.json();
                return json.filters ?? undefined;
            } catch {
                return undefined;
            }
        },
        [],
    );

    const fetchWithFilters = useCallback(
        async (
            filters: Record<string, string[]>,
            parentCategoryId?: string,
        ): Promise<ICategory[] | undefined> => {
            try {
                const response = await fetchModelSelection(endpoint, { filters, parentCategoryId });
                if (!response.ok) return undefined;
                const json = await response.json();
                return json.categories ?? json ?? [];
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('fetchWithFilters Error:', err);
                return undefined;
            }
        },
        [endpoint],
    );

    return {
        data,
        loading,
        error,
        fetchData,
        loadSubranges,
        loadSearchResults,
        loadFilters,
        fetchWithFilters,
    };
}
